/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, PaletteIcon, BadgeCheckIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { StyleReport } from '../types';

interface StyleReportModalProps {
    report: StyleReport | null;
    isOpen: boolean;
    onClose: () => void;
    onSave?: (report: StyleReport) => void;
}

const StyleReportModal: React.FC<StyleReportModalProps> = ({ report, isOpen, onClose, onSave }) => {
    const { t } = useTranslation();
    if (!report || !isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    className="bg-gray-50 dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700"
                    onClick={(e) => e.stopPropagation()}
                >
                    <header className="p-6 border-b dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
                        <div>
                            <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-white">{t('style_report_title', 'Comprehensive Style Report')}</h2>
                            <p className="text-gray-500 text-sm">AI-Powered Multi-Agent Feedback</p>
                        </div>
                        <button onClick={onClose} title={t('common_close')} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors">
                            <XIcon className="w-6 h-6 text-gray-500" />
                        </button>
                    </header>

                    <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
                        {/* Summary Section */}
                        <section>
                            <div className="flex items-center gap-4 mb-4">
                                <div className="bg-indigo-600 text-white w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold font-serif shadow-lg">
                                    {report.score}
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 dark:text-white uppercase tracking-wider text-xs">{t('report_total_score', 'Overall Style Score')}</h3>
                                    <p className="text-gray-600 dark:text-gray-300 italic">"{report.summary}"</p>
                                </div>
                            </div>
                        </section>

                        {/* Color Analysis Section */}
                        <section className="bg-indigo-50/50 dark:bg-indigo-900/10 p-5 rounded-2xl border border-indigo-100 dark:border-indigo-900/30">
                            <h3 className="font-bold text-indigo-900 dark:text-indigo-300 flex items-center gap-2 mb-3 mt-0">
                                <PaletteIcon className="w-5 h-5" /> {t('report_color_harmony', 'Color Harmony')}
                            </h3>
                            <div className="flex items-center gap-2 mb-2">
                                <BadgeCheckIcon className={`w-5 h-5 ${report?.colorAnalysis?.matchesSeason ? 'text-green-500' : 'text-yellow-500'}`} />
                                <span className="font-semibold text-gray-800 dark:text-gray-200">
                                    {report?.colorAnalysis?.season || 'Season'}: {report?.colorAnalysis?.matchesSeason ? 'Matching' : 'Contrasting'}
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{report.colorAnalysis.colorHarmony}</p>
                            <div className="flex flex-wrap gap-2">
                                {(report?.colorAnalysis?.recommendations || []).map((rec: string, i: number) => (
                                    <span key={i} className="text-xs px-3 py-1 bg-white dark:bg-gray-800 border border-indigo-200 dark:border-indigo-800 rounded-full text-indigo-600 dark:text-indigo-400">
                                        {rec}
                                    </span>
                                ))}
                            </div>
                        </section>

                        {/* Agent Feedback Grid */}
                        <section>
                            <h3 className="font-bold text-gray-900 dark:text-white mb-4">{t('report_agent_reviews', 'Advisor Reviews')}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {(report?.agentReviews || []).map((agent: any, i: number) => (
                                    <div key={i} className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="font-bold text-xs text-indigo-600 dark:text-indigo-400 uppercase">{agent.agentName}</span>
                                            <div className="flex text-yellow-400">
                                                {[...Array(5)].map((_, star) => (
                                                    <span key={star} className={star < (agent.rating || 0) ? 'opacity-100' : 'opacity-30'}>★</span>
                                                ))}
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-4 leading-relaxed">"{agent.feedback}"</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Fit Check Section */}
                        <section className="bg-gray-50 dark:bg-gray-800 p-5 rounded-2xl">
                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-3 mt-0">
                                <BadgeCheckIcon className="w-5 h-5" /> {t('report_fit_summary', 'Reality Fit Check')}
                            </h3>
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Status: {report?.fitSummary?.overallStatus || 'Normal'}</p>
                            <div className="flex flex-wrap gap-2">
                                {(report?.fitSummary?.criticalAreas || []).map((area: string, i: number) => (
                                    <span key={i} className="text-[10px] uppercase font-bold px-2 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded">
                                        {area}
                                    </span>
                                ))}
                            </div>
                        </section>

                        {/* User Review Integration */}
                        <section className="border-t dark:border-gray-800 pt-6">
                            <h3 className="font-bold text-gray-900 dark:text-white mb-2">{t('report_user_feedback', 'Your Review')}</h3>
                            <p className="text-xs text-gray-500 mb-4">How accurate was this AI assessment?</p>
                            <div className="flex gap-4">
                                <button title="Accurate" className="flex-1 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Accurate
                                </button>
                                <button title="Inaccurate" className="flex-1 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Inaccurate
                                </button>
                            </div>
                        </section>
                    </div>

                    <footer className="p-6 border-t dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex flex-col sm:flex-row gap-4">
                        {onSave && (
                            <button
                                onClick={() => {
                                    onSave(report);
                                }}
                                className="flex-1 bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 px-6 py-2 rounded-full font-bold border-2 border-indigo-600 dark:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors shadow-md"
                            >
                                {t('report_save_to_styles', 'Save to My Styles')}
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="flex-1 bg-indigo-600 text-white px-8 py-2 rounded-full font-bold hover:bg-indigo-700 transition-colors shadow-lg"
                        >
                            {t('common_close')}
                        </button>
                    </footer>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default StyleReportModal;
